import React, { useState } from 'react'
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
  makeStyles,
  TextField,
  Box
} from '@material-ui/core'
import alertIcon from 'src/assets/images/alertIcon.svg'
import InputAdornment from '@material-ui/core/InputAdornment'
import { useInactivePageContext } from '../hooks/InactivePageLogicContext'
import { useMutation } from '@tanstack/react-query'
import { validateMobileNumber } from 'src/graphql/queries'
import { API, graphqlOperation } from 'aws-amplify'
import { userSendSmsOtp } from 'src/graphql/mutations'

const useStyles = makeStyles(() => ({
  root: {
    textAlign: 'center'
    // fontFamily: "DIN Regular Alternate",
  },
  dialogTitle: {
    marginTop: '0.5rem',
    marginBottom: '-0.7rem'
  },
  dialogContent: {
    fontSize: '14px'
  },
  dialogButton: {
    display: 'flex',
    justifyContent: 'center',
    marginBottom: '0.5rem'
  },
  dialogButtonText: {
    borderRadius: '25px',
    textTransform: 'capitalize',
    height: '40px',
    maxWidth: '145px',
    width: '100%'
  },
  paperClassName: {
    borderRadius: '20px',
    minHeight: '150px',
    minWidth: '40%',
    display: 'flex',
    justifyContent: 'center'
  }
}))

const MobileDialog = (props) => {
  // Mobile number validation function
  const isValidMobile = (num) => {
    // Accepts Malaysian mobile numbers without country code
    // Should start with 1 and be 9 or 8 digits (e.g., 1XXXXXXXX or 1XXXXXXX)
    return /^1[0-9]{8,9}$/.test(num)
  }
  const [step, setStep] = useState(1)
  const [error, setError] = useState(null)

  const classes = useStyles()

  const {
    verificationPlatform,
    setVerificationPlatform,
    setVerificationPage,
    mobileNo,
    setMobileNo,
    isUpdatingProfile,
    forgotPassword,
    setForgotPassword,
    setTimer
  } = useInactivePageContext()

  const stepTitle = {
    2: 'Would you like to save this as your primary phone number?',
    3: 'The phone number already exists.\nWould you like to continue using it as a temporary number?'
  }
  const stepCancel = {
    2: 'Skip',
    3: 'No'
  }
  const stepSave = {
    2: 'Save',
    3: 'Yes'
  }

  // onConfirm needed for Pop Up in ongoingOrderDetails page
  const { open, setOpen } = props

  const {
    mutate: mutateUserSendSmsOtp,
    isPending: isSendingSmsOtp
  } = useMutation({
    mutationFn: async (mobileNo) => {
      const res = await API.graphql(
        graphqlOperation(userSendSmsOtp, {
          mobileNo: `60${mobileNo}`,
          platform: verificationPlatform,
          forgotPassword: forgotPassword
        })
      )
      setTimer(res?.data?.userSendSmsOtp?.RemainingTime || 0)
      return res?.data?.userSendSmsOtp
    },
    onSuccess(data) {
      if (data?.Status === true) {
        setStep(1)
        setVerificationPage((prev) => ({ ...prev, open: true }))
      } else {
        if (
          data?.Message.includes(
            'Mobile Phone No. is already associated with another account.'
          )
        ) {
          setStep(3)
          setOpen(true)
        } else {
          setStep(1)
          setError(data?.Message || 'Failed to send OTP')
          setOpen(true)
        }
      }
    }
  })

  const {
    mutate: mutateValidateMobileNumber,
    isPending: isValidatingMobileNumber
  } = useMutation({
    mutationFn: async (mobileNo) => {
      const res = await API.graphql(
        graphqlOperation(validateMobileNumber, { mobileNo: `60${mobileNo}` })
      )

      return res?.data?.validateMobileNumber
    },
    async onSuccess(data) {
      if (data?.statusCode === 200) {
        setStep(3)
        setForgotPassword(Boolean(data?.statusCode === 200))
      } else {
        setStep(2)
      }
    }
  })

  const handleSave = async () => {
    if (step === 2) {
      //save as primary number and send otp
      try {
        setVerificationPage((prev) => ({ ...prev, update: true }))
        await mutateUserSendSmsOtp(mobileNo)
      } catch (e) {
        setStep(1)
        setError('Failed to send OTP')
      }
    } else if (step === 3) {
      //number already exists, send otp only
      setVerificationPage((prev) => ({ ...prev, update: false }))
      await mutateUserSendSmsOtp(mobileNo)
    }
  }

  const handleCancel = async () => {
    if (step === 2) {
      //dont save as primary number, ssend otp only
      setVerificationPage((prev) => ({ ...prev, update: false }))
      await mutateUserSendSmsOtp(mobileNo)
    } else if (step === 3) {
      //use another number
      setStep(1)
    }
  }

  const loading =
    isUpdatingProfile || isValidatingMobileNumber || isSendingSmsOtp

  if (step === 1) {
    return (
      <Dialog
        open={open}
        onClose={(e, reason) => {
          if (reason && reason === 'backdropClick') return
          setOpen(false)
        }}
        aria-labelledby="confirm-dialog"
        className={classes.root}
        PaperProps={{
          className: classes.paperClassName
        }}>
        <DialogTitle>
          <img
            src={alertIcon}
            alt="alert"
            style={{ width: '50px', height: '50px', marginBottom: '10px' }}
          />
          <Typography>
            Let’s verify your number before you proceed. It only takes a moment!
          </Typography>
        </DialogTitle>
        <DialogContent
          style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          <TextField
            disabled={step === 2}
            variant="outlined"
            fullWidth
            style={{ width: '100%' }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">+60</InputAdornment>
              ),
              style: { height: '40px' }
            }}
            value={mobileNo}
            onChange={(e) => setMobileNo(e.target.value)}
          />

          <Typography style={{ marginBottom: '10px' }}>
            {error && <span style={{ color: 'red' }}>{error}</span>}
          </Typography>

          <Typography
            style={{
              color: '#7B7E8E',
              fontWeight: 'normal',
              fontSize: '16px'
            }}>
            Receive 4-digits code via:
          </Typography>
          <Button
            disabled={isValidatingMobileNumber}
            style={{ borderRadius: '10px' }}
            variant="contained"
            color="primary"
            onClick={() => {
              mutateValidateMobileNumber(`${mobileNo}`)
              setVerificationPlatform('SMS')
            }}>
            SMS
          </Button>

          {/* <Button
            disabled={isValidatingMobileNumber}
            variant="text"
            style={{
              color: '#7B7E8E',
              fontWeight: 'normal',
              fontSize: '16px'
            }}
            onClick={() => {
              mutateValidateMobileNumber(`${mobileNo}`)
              setVerificationPlatform('Whatsapp')
            }}>
            <span style={{ textTransform: 'none' }}>
              or&nbsp;
              <span
                style={{ textDecoration: 'underline', textTransform: 'none' }}>
                Whatsapp
              </span>
            </span>
          </Button> */}
        </DialogContent>
        <DialogActions className={classes.dialogButton}>
          <Button
            onClick={() => setOpen(false)}
            style={{
              textDecoration: 'underline',
              textTransform: 'none',
              color: '#1E91D0',
              fontWeight: 'normal',
              fontSize: '14px'
            }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    )
  } else {
    return (
      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        aria-labelledby="confirm-dialog"
        className={classes.root}
        PaperProps={{
          className: classes.paperClassName
        }}>
        <DialogTitle>
          <Box
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '10px'
            }}>
            <img
              src={alertIcon}
              alt="alert"
              style={{ width: '50px', height: '50px', marginBottom: '10px' }}
            />
            <Typography> {stepTitle[step]}</Typography>

            <Typography>
              {error && step === 1 && (
                <span style={{ color: 'red' }}>{error}</span>
              )}
            </Typography>
          </Box>
        </DialogTitle>

        <DialogActions className={classes.dialogButton}>
          {step === 1 ? (
            <Button
              variant="contained"
              onClick={() => {}}
              color="primary"
              className={classes.dialogButtonText}>
              Confirm
            </Button>
          ) : (
            <div
              style={{
                display: 'flex',
                width: '100%',
                justifyContent: 'space-around'
              }}>
              {' '}
              <Button
                disabled={loading}
                variant="outlined"
                onClick={handleCancel}
                color="primary"
                className={classes.dialogButtonText}>
                {loading ? 'Loading...' : stepCancel[step]}
              </Button>
              <Button
                disabled={loading}
                variant="contained"
                onClick={handleSave}
                color="primary"
                className={classes.dialogButtonText}>
                {loading ? 'Loading...' : stepSave[step]}
              </Button>
            </div>
          )}
        </DialogActions>
      </Dialog>
    )
  }
}
export default MobileDialog
